import { API_PREFIX, HttpError, type HttpClient } from "@nasam/kernel";
import {
  ProfileRequestError,
  type ProfileRepository,
  type ProfileUpdateInput,
  type UserProfile,
} from "../domain";
import {
  changePasswordResponseDtoSchema,
  profileResponseDtoSchema,
  type ProfileDto,
  type ProfileUpdateDto,
} from "./profileDtos";

const PROFILE_PATH = `${API_PREFIX}/profile`;
const CHANGE_PASSWORD_PATH = `${API_PREFIX}/auth/change-password`;
const TWO_FACTOR_PATH = `${API_PREFIX}/auth/2fa`;

/**
 * POST /auth/2fa/enable/verify answers every rejected code with one 400, told
 * apart only by its message (the swagger lists the three wordings).
 */
function twoFactorRejection(error: HttpError): ProfileRequestError {
  const message = error.message.toLowerCase();
  if (message.includes("expired")) {
    return new ProfileRequestError("verificationCodeExpired", error.message);
  }
  if (message.includes("maximum number of attempts")) {
    return new ProfileRequestError(
      "verificationAttemptsExceeded",
      error.message,
    );
  }
  return new ProfileRequestError("invalidVerificationCode", error.message);
}

/** A 409 means 2FA is already where the user asked for it to be. */
async function ignoreAlreadyThere(request: Promise<unknown>): Promise<void> {
  try {
    await request;
  } catch (error) {
    if (error instanceof HttpError && error.status === 409) return;
    throw error;
  }
}

export function profileFromDto(dto: ProfileDto): UserProfile {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    nameEn: dto.nameEn ?? "",
    nameAr: dto.nameAr ?? "",
    phoneNumber: dto.phoneNumber ?? "",
    company:
      dto.company === null || dto.company === undefined
        ? null
        : {
            nameEn: dto.company.nameEn ?? "",
            nameAr: dto.company.nameAr ?? "",
          },
    twoFactorEnabled: dto.enable2FA ?? false,
    lastPasswordChangedAt: dto.passwordChangedAt ?? null,
    avatarUrl: dto.profilePictureUrl ?? null,
  };
}

function profileUpdateToDto(input: ProfileUpdateInput): ProfileUpdateDto {
  return {
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    phoneNumber: input.phoneNumber,
    ...(input.avatar.kind === "remove" ? { removeProfilePicture: true } : {}),
  };
}

// Only used when a picture is being uploaded; otherwise a JSON body works too
// (see the PATCH /profile swagger note).
function profileMultipartBody(dto: ProfileUpdateDto, file: File): FormData {
  const body = new FormData();
  for (const [key, value] of Object.entries(dto)) {
    if (value !== undefined) body.set(key, String(value));
  }
  body.set("profilePicture", file, file.name);
  return body;
}

/**
 * GET/PATCH /profile for the profile itself, PATCH /auth/change-password for
 * the password and /auth/2fa/* for two-factor. A 400/409/413/415 from PATCH
 * /profile surfaces as an HttpError with the backend's localized message (the
 * global error toast shows it).
 */
export function createProfileHttpRepository(
  http: HttpClient,
): ProfileRepository {
  async function getMyProfile(): Promise<UserProfile> {
    const response = await http.get(PROFILE_PATH);
    return profileFromDto(profileResponseDtoSchema.parse(response).data);
  }

  return {
    getMyProfile,

    async updateMyProfile(input) {
      const dto = profileUpdateToDto(input);
      const response = await http.patch(
        PROFILE_PATH,
        input.avatar.kind === "replace"
          ? profileMultipartBody(dto, input.avatar.file)
          : dto,
      );
      return profileFromDto(profileResponseDtoSchema.parse(response).data);
    },

    // The refreshToken cookie rides along by itself and tells the server
    // which session to keep.
    async changePassword(input) {
      try {
        const response = await http.patch(CHANGE_PASSWORD_PATH, {
          currentPassword: input.currentPassword,
          newPassword: input.newPassword,
          confirmPassword: input.confirmPassword,
        });
        const { data } = changePasswordResponseDtoSchema.parse(response);
        return data === null || data === undefined
          ? { session: "ended" }
          : { session: "kept", accessToken: data.accessToken };
      } catch (error) {
        // One 400 for every refusal (wrong or reused current password, policy,
        // mismatch) -- told apart only by its localized message, which the
        // screen shows as is rather than guessing a field from the wording.
        if (error instanceof HttpError && error.status === 400) {
          throw new ProfileRequestError("passwordRejected", error.message);
        }
        throw error;
      }
    },

    // A 429 (hourly code budget spent) carries the wait in its message, which
    // the global error toast shows.
    async requestTwoFactorCode() {
      await http.post(`${TWO_FACTOR_PATH}/enable`);
    },

    // Neither switch returns the profile, so it's read back afterwards.
    async enableTwoFactor(code) {
      try {
        await ignoreAlreadyThere(
          http.post(`${TWO_FACTOR_PATH}/enable/verify`, { code }),
        );
      } catch (error) {
        if (error instanceof HttpError && error.status === 400) {
          throw twoFactorRejection(error);
        }
        throw error;
      }
      return getMyProfile();
    },

    async disableTwoFactor() {
      await ignoreAlreadyThere(http.post(`${TWO_FACTOR_PATH}/disable`));
      return getMyProfile();
    },
  };
}
